import JSZip from 'jszip';

import {
  attribute,
  canStream,
  cellText,
  columnIndex,
  openWorkbook,
  parseSharedStrings,
  SHARED_STRINGS_PART,
} from '../hubbell-feedback/peekWorkbook';

/**
 * A few named columns of one sheet of an `.xlsx`, read row by row.
 *
 * The ABB Results Workbook is 50 MB with 272k lines over 102 columns; a
 * whole-workbook parser holds every cell of it and stalls the page. The
 * report needs four of those columns, so the sheet's XML is inflated chunk
 * by chunk, each complete `<row>` is reduced to the wanted cells as it goes
 * by, and the rest is dropped. Same zip reading as `peekWorkbook`.
 *
 * Text only: numbers come back as written, formulas as their cached value.
 */

export interface SheetColumnsRequest {
  /** Headers to return, in this order; the header row must carry every one. */
  headers: readonly string[];
  /** How many rows from the top the header row may sit on (ABB: under a UoM row). */
  headerRows: number;
}

export interface SheetColumns {
  sheetName: string;
  /** One entry per data row below the header, cells in `headers` order. */
  rows: string[][];
}

/** Case and padding do not distinguish headers, as the feedback reader treats them. */
function normalizeHeader(cell: string): string {
  return cell.trim().toLowerCase();
}

type CellText = (attributes: string, inner: string) => string;

/** One `<row>`'s cells by column index; columns no cell names are absent. */
function rowCells(rowXml: string, read: CellText): Map<number, string> {
  const cells = new Map<number, string>();
  let next = 0;
  for (const cell of rowXml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
    const attributes = cell[1] ?? '';
    const reference = attribute(attributes, 'r');
    const column = reference === null ? next : columnIndex(reference);
    if (column === null) continue;
    cells.set(column, read(attributes, cell[2] ?? ''));
    next = column + 1;
  }
  return cells;
}

/** Where each wanted header sits in this row, or null when one is missing. */
function headerPositions(
  cells: Map<number, string>,
  headers: readonly string[],
): number[] | null {
  const byName = new Map<string, number>();
  for (const [column, text] of cells) {
    const name = normalizeHeader(text);
    if (!byName.has(name)) byName.set(name, column);
  }
  const positions: number[] = [];
  for (const header of headers) {
    const column = byName.get(normalizeHeader(header));
    if (column === undefined) return null;
    positions.push(column);
  }
  return positions;
}

const ROW_PATTERN = /<row\b[^>]*?(?:\/>|>([\s\S]*?)<\/row>)/g;

/**
 * The end of the last `</row>` in `xml`, or 0 when none has closed yet. An
 * empty self-closing row after it waits in the buffer for the next one; the
 * sheet's tail is read whole at the end.
 */
function lastRowEnd(xml: string): number {
  const closing = xml.lastIndexOf('</row>');
  return closing >= 0 ? closing + '</row>'.length : 0;
}

/** One sheet's rows as they stream by: header first, then the wanted cells. */
class SheetScan {
  private seen = 0;
  private positions: number[] | null = null;
  readonly rows: string[][] = [];

  constructor(
    private readonly request: SheetColumnsRequest,
    private readonly read: CellText,
  ) {}

  /** False once the header was not found in time: the sheet is not the one. */
  take(xml: string): boolean {
    for (const match of xml.matchAll(ROW_PATTERN)) {
      const cells = rowCells(match[1] ?? '', this.read);
      if (this.positions === null) {
        this.seen += 1;
        this.positions = headerPositions(cells, this.request.headers);
        if (this.positions === null && this.seen >= this.request.headerRows) {
          return false;
        }
        continue;
      }
      if (cells.size === 0) continue;
      this.rows.push(this.positions.map((column) => cells.get(column) ?? ''));
    }
    return true;
  }

  get found(): boolean {
    return this.positions !== null;
  }
}

function scanSheet(
  entry: JSZip.JSZipObject,
  scan: SheetScan,
): Promise<boolean> {
  if (!canStream(entry)) {
    return entry.async('string').then((xml) => scan.take(xml) && scan.found);
  }
  return new Promise((resolve, reject) => {
    let buffer = '';
    let settled = false;
    const stream = entry.internalStream('string');
    const finish = (found: boolean) => {
      if (settled) return;
      settled = true;
      resolve(found);
    };
    stream
      .on('data', (chunk: string) => {
        if (settled) return;
        buffer += chunk;
        const end = lastRowEnd(buffer);
        if (end === 0) return;
        const complete = buffer.slice(0, end);
        buffer = buffer.slice(end);
        if (!scan.take(complete)) {
          stream.pause();
          finish(false);
        }
      })
      .on('end', () => finish(scan.take(buffer) && scan.found))
      .on('error', (error: Error) => {
        if (settled) return;
        settled = true;
        reject(error);
      })
      .resume();
  });
}

/**
 * The first sheet whose header row carries every requested header, read
 * row by row. Null when no sheet has that header within `headerRows` rows.
 */
export async function streamSheetColumns(
  file: Blob,
  request: SheetColumnsRequest,
): Promise<SheetColumns | null> {
  const { zip, sheets } = await openWorkbook(file);
  const stringsXml = await zip.file(SHARED_STRINGS_PART)?.async('string');
  const strings = stringsXml ? parseSharedStrings(stringsXml) : [];
  const read: CellText = (attributes, inner) =>
    cellText(attributes, inner, strings);
  for (const { name, path } of sheets) {
    const entry = zip.file(path);
    if (!entry) continue;
    const scan = new SheetScan(request, read);
    if (await scanSheet(entry, scan)) {
      return { sheetName: name, rows: scan.rows };
    }
  }
  return null;
}
